import { INFERENCE_STATE, ResultFile } from "./useFile";
import { DEFAULT_SERVICE_NAME } from "../constants/upload";
import { OacJsonResponse } from "../types/demo";
import { Ontology } from "../types/ontology";
import { FileUploadConfig, FileUploadCallbacks } from "../types/upload";
import { getModel } from "../utils/file";
import { endpointStore } from "../utils/mobx/EndpointStore";
import { envVarStore } from "../utils/mobx/EnvVarStore";
import { parsingTableStore } from "../utils/mobx/ParsingTableStore";
import { resultInteractionStore } from "../utils/mobx/ResultInteractionStore";
import { imageAsyncUploader } from "../utils/upload/asyncUploader";
import {
  handleInputValidation,
  handleUploadError,
} from "../utils/upload/errorHandler";
import { fetchResult } from "../utils/upload/resultFetcher";
import { addResultToFiles } from "../utils/upload/resultManager";
import { imageSyncUploader } from "../utils/upload/syncUploader";

// 파일 상태 업데이트
const updateFileStates = (
  fileUploadConfig: FileUploadConfig,
  newFiles: ResultFile[],
  newResults: OacJsonResponse[],
  currentFiles: ResultFile[],
  currentOrgResults: OacJsonResponse[],
  setFiles: (files: ResultFile[]) => void,
  setOrgResults: (results: OacJsonResponse[]) => void,
) => {
  if (fileUploadConfig.resultFileIdToUpdate && newFiles.length === 1) {
    // 기존 파일 업데이트
    const updatedFiles = currentFiles.map(file =>
      file.id === fileUploadConfig.resultFileIdToUpdate
        ? { ...file, ...newFiles[0], id: file.id }
        : file,
    );
    setFiles(updatedFiles);

    if (newResults.length === 1) {
      const updatedOrgResults = currentOrgResults.map((result, index) =>
        currentFiles[index].id === fileUploadConfig.resultFileIdToUpdate
          ? newResults[0]
          : result,
      );
      setOrgResults(updatedOrgResults);
    }
  } else {
    // 새 파일 추가
    const updatedNewFiles = newFiles.map((newFile, index) => ({
      ...newFile,
      originFile: fileUploadConfig.inputFiles[index],
    }));
    setFiles([...currentFiles, ...updatedNewFiles]);
    setOrgResults([...currentOrgResults, ...newResults]);
  }
};

// 온톨로지 업데이트
const updateOntologies = (newResults: OacJsonResponse[]) => {
  const ontologyResult = newResults.find(r => "ontologies" in r);
  if (ontologyResult && "ontologies" in ontologyResult) {
    parsingTableStore.setOntologies(
      ontologyResult.ontologies as unknown as Ontology[],
    );
  }
};

// 메인 파일 업로드 함수
export const fileUpload = async (
  fileUploadConfig: FileUploadConfig,
  fileUploadCallbacks: FileUploadCallbacks,
) => {
  const {
    inputFiles,
    models,
    selectedModelIndex,
    files: currentFiles,
    errorType,
    skipValidation = false,
    inferenceConfig,
  } = fileUploadConfig;

  const {
    setSelectedFileIndex,
    setFiles,
    setInferenceState,
    setIsOpen = () => {},
    setWorkPercentageVisible = () => {},
  } = fileUploadCallbacks;

  // 초기 검증
  if (!inputFiles || !getModel(models, selectedModelIndex)) {
    console.error("Invalid input: files or model not found.");
    return;
  }

  if (
    !handleInputValidation(
      inputFiles,
      errorType,
      skipValidation,
      fileUploadCallbacks,
    )
  ) {
    console.error("Input validation failed.");
    return;
  }

  // 업로드 시작
  setWorkPercentageVisible(true);
  setInferenceState(
    fileUploadConfig.resultFileIdToUpdate
      ? INFERENCE_STATE.UPDATE
      : INFERENCE_STATE.NEW,
  );
  setIsOpen(false);

  const newFiles: ResultFile[] = [];
  const newResults: OacJsonResponse[] = [];

  try {
    const { endpointType, tenantId } = endpointStore;
    const { orgResults: currentOrgResults, setOrgResults } =
      resultInteractionStore;
    const { syncUploader } = envVarStore;
    const selectedModel = getModel(models, selectedModelIndex)!;

    if (syncUploader === "true") {
      // 동기 업로드
      const syncResults = await imageSyncUploader(
        Array.from(inputFiles),
        selectedModel.url,
        selectedModel.type,
        selectedModel.serviceName || DEFAULT_SERVICE_NAME,
        inferenceConfig,
      );

      for (const [result, filename] of syncResults) {
        await addResultToFiles(
          result,
          filename,
          selectedModelIndex,
          inferenceConfig,
          endpointType,
          newResults,
          newFiles,
        );
      }
    } else {
      // 비동기 업로드
      const requestIds = await imageAsyncUploader(
        Array.from(inputFiles),
        tenantId,
        selectedModel.url,
        selectedModel.type,
        selectedModel.serviceName || DEFAULT_SERVICE_NAME,
      );

      const resultPromises = requestIds.map(async (requestId, idx) => {
        const result = await fetchResult(requestId);
        if (result && result.code !== 500) {
          await addResultToFiles(
            result,
            inputFiles[idx].name,
            selectedModelIndex,
            inferenceConfig,
            endpointType,
            newResults,
            newFiles,
          );
        } else {
          throw new Error("Inference failed");
        }
      });

      await Promise.all(resultPromises);
    }

    // 상태 업데이트
    updateFileStates(
      fileUploadConfig,
      newFiles,
      newResults,
      currentFiles,
      currentOrgResults,
      setFiles,
      setOrgResults,
    );
    updateOntologies(newResults);

    // 파일 선택 인덱스 설정
    const targetFileIndex = fileUploadConfig.resultFileIdToUpdate
      ? currentFiles.findIndex(
          f => f.id === fileUploadConfig.resultFileIdToUpdate,
        )
      : currentFiles.length + newFiles.length - 1;

    setSelectedFileIndex(targetFileIndex >= 0 ? targetFileIndex : 0);
  } catch (error: any) {
    console.error(error);
    handleUploadError(error, fileUploadCallbacks);
  } finally {
    setWorkPercentageVisible(false);
    setInferenceState(INFERENCE_STATE.IDLE);
  }
};

// 기타 필요한 함수들 export
export { imageAsyncUploader, imageSyncUploader, fetchResult };
